import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import type { SidebarNavItem } from '@/components/layout/app-shell'
import {
  AgentIcon,
  AppsIcon,
  CredentialsIcon,
  IdentityProviderIcon,
  ModelGatewayIcon,
  OrganizationIcon,
  OverviewIcon,
  SettingsIcon,
  StatisticsIcon,
} from '@/features/admin/shell/navigation-icons'
import { useHubAgentName } from '@/features/shared/brand/branding-hooks'
import { readStoredPortalAccount } from '@/lib/auth/session-storage'

export function useAdminNavigation(): SidebarNavItem[] {
  const { t } = useTranslation('shell')
  const hubAgentName = useHubAgentName()
  const isSystemAdmin = readStoredPortalAccount('admin').role === 'super_admin'

  return useMemo(
    () => [
      {
        icon: <OverviewIcon />,
        key: 'overview',
        label: t('adminNavigation.overview'),
        path: '/admin/overview',
        variant: 'strong',
      },
      {
        children: [
          { key: 'apps', label: t('adminNavigation.apps'), path: '/admin/apps' },
          {
            key: 'app-review',
            label: t('adminNavigation.appReview'),
            path: '/admin/app-review',
          },
          { key: 'app-tags', label: t('adminNavigation.appTags'), path: '/admin/app-tags' },
        ],
        icon: <AppsIcon />,
        key: 'applications',
        label: t('adminNavigation.applications'),
      },
      {
        children: [
          {
            key: 'departments',
            label: t('adminNavigation.departments'),
            path: '/admin/organization/departments',
          },
          { key: 'members', label: t('adminNavigation.members'), path: '/admin/members' },
          {
            key: 'organization-sync',
            label: t('adminNavigation.organizationSync'),
            path: '/admin/organization/sync',
          },
        ],
        icon: <OrganizationIcon />,
        key: 'organization',
        label: t('adminNavigation.organization'),
      },
      {
        children: [
          {
            key: 'idp-provider',
            label: t('adminNavigation.idpProvider'),
            path: '/admin/idp/provider',
          },
          {
            key: 'idp-resources',
            label: t('adminNavigation.idpResources'),
            path: '/admin/idp/resources',
          },
          {
            key: 'idp-clients',
            label: t('adminNavigation.idpClients'),
            path: '/admin/idp/clients',
          },
          {
            key: 'idp-client-scopes',
            label: t('adminNavigation.idpClientScopes'),
            path: '/admin/idp/client-scopes',
          },
        ],
        icon: <IdentityProviderIcon />,
        key: 'identity-provider',
        label: t('adminNavigation.idp'),
      },
      {
        children: [
          {
            key: 'model-provider',
            label: t('adminNavigation.modelProvider'),
            path: '/admin/model-provider',
          },
          {
            key: 'model-keys',
            label: t('adminNavigation.modelKeys'),
            path: '/admin/model-keys',
          },
          {
            key: 'model-call-logs',
            label: t('adminNavigation.modelCallLogs'),
            path: '/admin/model-call-logs',
          },
        ],
        icon: <ModelGatewayIcon />,
        key: 'model-gateway',
        label: t('adminNavigation.modelGateway'),
      },
      {
        children: [
          {
            key: 'hub-agent',
            label: hubAgentName,
            path: '/admin/agent/hub',
          },
          {
            key: 'mcp',
            label: t('adminNavigation.mcp'),
            path: '/admin/agent/mcp',
          },
        ],
        icon: <AgentIcon />,
        key: 'agent',
        label: t('adminNavigation.agent'),
      },
      {
        children: [
          {
            key: 'usage-dashboard',
            label: t('adminNavigation.usageDashboard'),
            path: '/admin/statistics/dashboard',
          },
          {
            key: 'conversation-logs',
            label: t('adminNavigation.conversationLogs'),
            path: '/admin/statistics/conversations',
          },
        ],
        icon: <StatisticsIcon />,
        key: 'statistics',
        label: t('adminNavigation.statistics'),
      },
      {
        children: [
          {
            key: 'adp-credentials',
            label: t('adminNavigation.adpCredentials'),
            path: '/admin/credentials/adp',
          },
          ...(isSystemAdmin
            ? [
                {
                  key: 'wecom-identity-profiles',
                  label: t('adminNavigation.wecomIdentityProfiles'),
                  path: '/admin/credentials/wecom',
                },
              ]
            : []),
        ],
        icon: <CredentialsIcon />,
        key: 'credentials',
        label: t('adminNavigation.credentials'),
      },
      {
        children: [
          {
            key: 'sso',
            label: t('adminNavigation.sso'),
            path: '/admin/settings/sso',
          },
          {
            key: 'admin-accounts',
            label: t('adminNavigation.adminAccounts'),
            path: '/admin/settings/admins',
          },
          {
            key: 'branding',
            label: t('adminNavigation.branding'),
            path: '/admin/settings/branding',
          },
          {
            key: 'business-navigation',
            label: t('adminNavigation.businessNavigation'),
            path: '/admin/settings/business-navigation',
          },
        ],
        icon: <SettingsIcon />,
        key: 'settings',
        label: t('adminNavigation.settings'),
      },
    ],
    [hubAgentName, isSystemAdmin, t],
  )
}
